/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment } from 'react';
import {
  Modal,
  Input,
  Checkbox,
  Table,
  Switch,
  Tag,
  Collapse,
  message,
  ColorPicker,
  Form,
  Select,
  DatePicker,
} from 'antd';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ModalProps } from 'antd/lib/modal';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { useMemo, CSSProperties, useState } from 'react';
import IconButton from '~components/_common/IconButton/IconButton';
import { fadeInKeyframes } from '~src/styles/animations';
import { DB_STATE } from '~src/types/enums';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { appLog } from '~src/utils/log';
import { useTenderFolders } from '~src/hooks/api-hooks/useTenderFolders';
import { DbFolder, DbTenderFolder } from '~src/types/db';
import { queryCache, useMutation } from 'react-query';
import { __DEV__ } from '~src/config';
import { useProjectId } from '~src/hooks/useProjectId';
import { Controller, useForm } from 'react-hook-form';
import isEmail from 'validator/es/lib/isEmail';
import { SelectProps } from 'antd/lib/select';
import { uniq } from 'lodash';
import type { ColumnsType } from 'antd/es/table';

export interface ResendInviteModalProps extends Partial<ModalProps> {
  parent?: DbFolder;
}

export const ResendInviteModal: React.FC<ResendInviteModalProps> = ({
  parent,
  onCancel,
  ...modalProps
}) => {
  const { Panel } = Collapse;
  const projectId = useProjectId();
  const { TextArea } = Input;

  const intl = useIntl();
  const style = useStyles(styleFn);
  const getDefaultEmails = () => (__DEV__ ? [] : []);
  const [searchValue, setSearchValue] = useState('');
  const [emails, setEmails] = useState<string[]>(getDefaultEmails);

  const invalidEmails = useMemo(() => emails.filter((val) => !isEmail(val)), [
    emails,
  ]);

  const isValid = () => {
    if (!emails.length || invalidEmails.length) return false;

    return true;
  };

  // const linkedFolders = useMemo(
  //   () =>
  //     foldersQuery.data?.filter(
  //       (folder) => folder.options.tender_registration_id !== null
  //     ),
  //   [foldersQuery.data]
  // );
  //
  // const availableFolders = useMemo(
  //   () =>
  //     foldersQuery.data?.filter(
  //       (folder) =>
  //         folder.options.tender_registration_id === null &&
  //         folder.name.toLowerCase().includes(searchValue.toLowerCase())
  //     ),
  //   [foldersQuery.data, searchValue]
  // );
  // const getColorRow = (record) =>
  //   Number(record.state) === DB_STATE.REMOVED ? '#ffd4d9' : '';

  const [invintationBlocked, setInvintationBlocked] = useState<boolean>(true);

  const toggleBlocker = () => {
    setInvintationBlocked((prev) => !prev);
  };

  const sharesForm = useForm({
    defaultValues: {
      email: '',
      title: '',
      message: '',
    },
  });

  interface MockVenderType {
    key: React.Key;
    title: string;
    email: string;
  }

  const mockVenders: MockVenderType[] = [
    {
      key: '0',
      title: 'Vendor A',
      email: 'a@example.com',
    },
    {
      key: '1',
      title: 'Vendor B',
      email: 'b@example.com',
    },
    {
      key: '2',
      title: 'Vendor C',
      email: 'c@example.com',
    },
  ];

  const columns: ColumnsType<MockVenderType> = [
    {
      key: 'title',
      title: 'title',
      dataIndex: 'title',
      width: 50,
      render: (text, record) => {
        return <div css={style.actionContainer}>{record.title}</div>;
      },
    },
    {
      key: 'email',
      title: 'email',
      dataIndex: 'email',
      width: 50,
      render: (text, record) => {
        return (
          <div css={style.actionContainer} style={{ color: 'grey' }}>
            {record.email}
          </div>
        );
      },
    },
  ];

  const handleSubmit = async () => {
    try {
      message.error('Not implemented yet');
    } catch (err) {
      message.error(intl.formatMessage({ id: 'share-edit-failed' }));
    }
  };

  return (
    <Modal
      title={
        <div css={style.titleContainer}>
          <IconButton iconStyle="far" icon="paper-plane" backgroundOff />
          {intl.formatMessage({ id: 'resend-invite' })}
        </div>
      }
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width="min(75vw, 500px)"
      bodyStyle={{ padding: '0 25px 0 25px' }}
      centered
      destroyOnClose
      maskClosable={false}
      onCancel={onCancel}
      okText={intl.formatMessage({ id: 'save' })}
      onOk={handleSubmit}
      footer={
        window.sessionStorage.getItem('appState') !== 'timeMachine' && undefined
      }
      {...modalProps}>
      <div css={style.container}>
        <p>{intl.formatMessage({ id: 'recipient' })}</p>
        <Collapse
          defaultActiveKey={['1']}
          expandIcon={() => <div style={{ display: 'none' }} />}
          ghost
          style={{
            padding: 0,
            margin: 0,
          }}>
          <Panel header={<div style={{ padding: '0' }}></div>} key="1">
            <Table<MockVenderType>
              pagination={false}
              columns={columns}
              dataSource={mockVenders}
              rowKey="key"
              showHeader={false}
              scroll={{ y: 200 }}
            />
          </Panel>
        </Collapse>
        <Form>
          <div
            css={style.otherRecipientsContainer}
            style={{ marginTop: '20px' }}>
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
              }}>
              {intl.formatMessage({ id: 'invitation' })}
              <span
                css={style.buttonDiv}
                style={{ gap: '5px' }}
                onClick={toggleBlocker}>
                <FontAwesomeIcon icon={['far', 'pen']} />
                {intl.formatMessage({ id: 'edit' })}
              </span>
            </div>
            <div style={{ marginBottom: '10px' }}>
              {intl.formatMessage({ id: 'title' })}
              <Controller
                name="title"
                control={sharesForm.control}
                render={({ field }) => (
                  <Input disabled={invintationBlocked} {...field} />
                )}
              />
            </div>
            <div>
              {intl.formatMessage({ id: 'message' })}
              <Controller
                name="message"
                control={sharesForm.control}
                render={({ field }) => (
                  <TextArea
                    disabled={invintationBlocked}
                    autoSize={{ minRows: 4, maxRows: 4 }}
                    {...field}
                  />
                )}
              />
            </div>
            <div>
              <p>Send at @</p>
              <DatePicker
                css={{
                  minWidth: '50%',
                  marginBottom: 16,
                }}
                showTime
                showNow={false}
                format="DD-MM-YYYY, HH:mm"
                // disable days before today
                // if today selected, disable past hours
                value={() => {
                  ('');
                }}
                onSelect={() => {
                  ('');
                }}
              />
            </div>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'flex-start',
  },
  iconRemove: {},
  rowContainer: { marginBottom: '6px' },

  actionContainer: {
    cursor: 'pointer',
  },
  containerSearch: {
    width: '50%',
    // display: 'flex',
    // alignItems: 'center',
    // border: `1px solid ${theme.colors.lightGray}`,
    // borderRadius: '2px',
    // margin: '8px 0',
    // paddingLeft: '5px',
    // width: '55%',
    // height: '32px',
    // ':focus': {
    //   border: `1px solid ${theme.colors.primary}`,
    // },
    // // backgroundColor: theme.colors.gray,
  },
  icons: {
    color: theme.colors.text.primary,
    fontSize: '16px',
  },
  inputField: {
    // border: 'none!important',
    // height: '100%',
    // width: '100%',
    // color: `${theme.colors.text.primary}`,
    //
    // '::placeholder': {
    //   color: `${theme.colors.text.secondary}`,
    // },
    //
    // ':focus': {
    //   outline: 'none',
    // },
  },
  buttonDiv: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    type: 'button',
    cursor: 'pointer',
    marginLeft: 4,
    padding: '2px 10px',
    ':hover': {
      backgroundColor: theme.colors.gray,
      borderRadius: 9,
    },
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  otherRecipientsContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  tag: {
    borderRadius: 4,
    marginRight: 3,
    fontSize: 14,
    animation: `${fadeInKeyframes} 0.2s ease-out`,
  },
});
